import { memo, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { Box, Button, Typography } from '@mui/material';
import type { Nullable, NullableFields } from '@provider-portal/common/types/types';

import { getCurrentDayStart } from 'utils/date';
import { $tk } from 'utils/appTranslationKey';

import { StartEndDatePicker } from 'components/StartEndDatePicker/StartEndDatePicker';

import { Promotion, PromotionDuration } from '../../types';
import { PromotionFooter } from '../PromotionFooter/PromotionFooter';
import { getEndRestrictions, getStartRestrictions, isNonNullable, MinMaxDate } from '../utils';

export interface AddPromotionDurationFCProps {
  strings: {
    title: string;
    description: string;
    continue: string;
    back: string;
  };
  promotion: Promotion;
  initialValue?: PromotionDuration;
  hideDescription?: boolean;
  onClickNext: (duration: PromotionDuration) => void;
  onClickBack: () => void;
}

const removeSeconds = (value: Nullable<number>) => {
  if (value === null) {
    return null;
  }
  const date = new Date(value);
  date.setSeconds(0, 0);
  return date.getTime();
};

const getAdjustedDateTime = (value: Nullable<number>) => {
  // if input date is todays date then return timestamp of current time (not start of day)
  return value === null ? null : Math.max(value, Date.now());
};

const checkDate = ({ minDate, maxDate }: MinMaxDate, date: number | null): boolean => {
  if (process.env.REACT_APP_STAGE !== 'live') {
    // Needs to test different cases by QA. Can be used on plelive/test only
    if (localStorage.getItem('bolt_testing.__disablePromotionDurationCheck') === 'yes') {
      return true;
    }
  }
  return isNonNullable(date) && date >= minDate && date <= maxDate;
};
const ONE_DAY_MS = 24 * 3600 * 1000;
export const AddPromotionDurationFC = memo(
  ({
    strings,
    promotion,
    initialValue,
    hideDescription,
    onClickNext,
    onClickBack,
  }: AddPromotionDurationFCProps) => {
    const intl = useIntl();

    const pickerStrings = useMemo(
      () => ({
        start: intl.formatMessage({ id: $tk.widgets.AddPromotion.start }),
        startDescription: intl.formatMessage({ id: $tk.widgets.AddPromotion.startDescription }),
        end: intl.formatMessage({ id: $tk.widgets.AddPromotion.end }),
        endDescription: intl.formatMessage({ id: $tk.widgets.AddPromotion.endDescription }),
        minEndDateMessageError: intl.formatMessage({
          id: $tk.widgets.AddPromotion.minEndDateMessageError,
        }),
      }),
      [intl],
    );
    const [duration, setDuration] = useState<NullableFields<PromotionDuration>>(
      initialValue ?? {
        start: Math.max(getCurrentDayStart().getTime(), promotion.startDate),
        end: null,
      },
    );

    const onClickBackHandler = useCallback(() => onClickBack(), [onClickBack]);

    const onDurationChanged = useCallback((start: Nullable<number>, end: Nullable<number>) => {
      setDuration({ start: getAdjustedDateTime(start), end: removeSeconds(end) });
    }, []);

    const startPickerMinMax = useMemo(() => getStartRestrictions(promotion), [promotion]);

    const endPickerMinMax = useMemo(
      () => getEndRestrictions(promotion, startPickerMinMax, duration.start),
      [duration.start, promotion, startPickerMinMax],
    );

    const isContinueDisabled = useMemo(
      () =>
        !checkDate(startPickerMinMax, duration.start) || !checkDate(endPickerMinMax, duration.end),
      [duration, endPickerMinMax, startPickerMinMax],
    );

    const endPickerMaxDateToShow = useMemo(
      // The last day on picker will always be one day less than possible calculated limit as there might be a case that
      // endPickerMaxDate is calculated to campaign.end and campaign is not exactly ending on mid night so its safer to allow
      // providers to create campaigns a day less than actual possible end date.
      () => endPickerMinMax.maxDate - ONE_DAY_MS,
      [endPickerMinMax.maxDate],
    );

    const onClickContinue = useCallback(() => {
      const { start, end } = duration;
      if (start && end) {
        onClickNext({ start, end });
      }
    }, [duration, onClickNext]);

    return (
      <Box>
        <Box mb="8px">
          <Typography variant="h6">{strings.title}</Typography>
        </Box>
        {!hideDescription && <Box mb={3}>{strings.description}</Box>}
        <Box mb={3} width="75%">
          <StartEndDatePicker
            strings={pickerStrings}
            startDate={duration.start}
            endDate={duration.end}
            startPickerProps={startPickerMinMax}
            endPickerProps={{ ...endPickerMinMax, maxDate: endPickerMaxDateToShow }}
            onChange={onDurationChanged}
          />
        </Box>
        <PromotionFooter>
          <Box mr={1}>
            <Button variant="contained" color="secondary" onClick={onClickBackHandler}>
              {strings.back}
            </Button>
          </Box>
          <Box>
            <Button
              variant="contained"
              color="primary"
              onClick={onClickContinue}
              disabled={isContinueDisabled}
            >
              {strings.continue}
            </Button>
          </Box>
        </PromotionFooter>
      </Box>
    );
  },
);
